import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import BrandLoader from '../shared/brand/BrandLoader';
import { getIntlLocale } from '../../utils/functions';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Header from '../shared/TailwindComponents/Header';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import Table from '../shared/TailwindComponents/Table';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import {
  PiTableLight,
  PiPlusLight,
  PiTrashLight,
  PiPlayLight,
  PiDownloadSimpleLight,
  PiArrowCounterClockwiseLight,
  PiSpinnerGapLight,
  PiHourglassLight,
  PiClockLight,
  PiClockCounterClockwiseLight, PiBriefcaseLight } from 'react-icons/pi';
import { hideModal, showModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import {
  fetchBatchTables,
  deleteBatchTable,
  deleteBatchTablesBulk,
  startProcessing,
  resetTable,
  exportTable,
} from '../../store/actions/BatchTables/BatchTablesActions';
import { TableListItem, TableStatus } from '../../types/batchTables';
import CreateTableForm from './CreateTableForm';
import DeleteTableConfirmation from './DeleteTableConfirmation';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import Button from '../shared/TailwindComponents/Button';
import ResourceTrash from '../RecycleBin/ResourceTrash';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import { useProcessingPubSub } from '../../hooks/useProcessingPubSub';
import MiniLoadingToCheckmark from '../shared/animations/MiniLoadingToCheckmark';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, tablesListTourSteps } from '../shared/FeatureTour';

import ActivityDrawer from '../shared/activity/ActivityDrawer';

// ─── Status → Badge color ────────────────────────────────────────────────────

const statusColorMap: Record<TableStatus, BadgeColor> = {
  pending: 'gray',
  queued: 'orange',
  processing: 'blue',
  completed: 'green',
  failed: 'red',
  partial: 'yellow',
  stopped: 'amber',
};

const statusLabelMap: Record<TableStatus, string> = {
  pending: 'Pending',
  queued: 'Queued',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
  partial: 'Partial',
  stopped: 'Stopped',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(getIntlLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
};

// ─── Component ───────────────────────────────────────────────────────────────

const BatchTablesList: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  // Redux state
  const {
    items,
    totalItems,
    currentPage,
    totalPages,
    isLoading,
  } = useSelector((state: RootState) => state.batchTables);

  const { checkSectionGate } = useLicenseGate();

  // Local state
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [auditTarget, setAuditTarget] = useState<{ type: string; id: string; name: string } | null>(null);
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isCreating = queryParams.get('add') === 'true';
  const openCreate = useCallback(() => {
    if (!checkSectionGate('tables')) return;
    const params = new URLSearchParams(location.search);
    params.set('add', 'true');
    history.push(`${location.pathname}?${params.toString()}`);
  }, [checkSectionGate, history, location.pathname, location.search]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showTrash, setShowTrash] = useState(false);
  // The archived view's count rides in the filter bar's own badge (R4-01).
  const [trashCount, setTrashCount] = useState<{ showing: number; total: number }>({ showing: 0, total: 0 });
  const [trashBulkActions, setTrashBulkActions] = useState<{ label: string; onClick: () => void; icon?: React.ReactElement }[]>([]);
  const [trashSelectedCount, setTrashSelectedCount] = useState(0);

  // ─── PubSub: live processing tracking ──────────────────────────────
  const fetchRef = useRef<() => void>(() => {});
  const activeStatuses = new Set<TableStatus>(['processing', 'queued', 'pending']);

  const { processingStates, joinProcessingGroups, reattachListener } = useProcessingPubSub({
    entityType: 'table',
    getGroupName: (item: TableListItem) => item.uuid, // tables use UUID directly as PubSub group
    getItemUuid: (item: TableListItem) => item.uuid,
    isProcessing: (item: TableListItem) => activeStatuses.has(item.status),
    onRefetch: () => fetchRef.current(),
    enabled: !isCreating,
  });

  // ─── Data Fetching (server-side search + sort) ──────────────────────────

  const fetchPage = useCallback(
    (page: number) => {
      dispatch(
        fetchBatchTables({
          page,
          page_size: 20,
          sort_by: sortBy,
          sort_order: sortOrder,
          ...(searchTerm ? { search_term: searchTerm } : {}),
        })
      );
    },
    [dispatch, sortBy, sortOrder, searchTerm]
  );

  // Keep fetchRef in sync for PubSub refetch callback
  useEffect(() => {
    fetchRef.current = () => fetchPage(1);
  }, [fetchPage]);

  // Join PubSub groups whenever items update
  useEffect(() => {
    if (!isCreating && items.length > 0) {
      joinProcessingGroups(items);
    }
  }, [items, isCreating, joinProcessingGroups]);

  // Re-attach PubSub listener when coming back from creation form
  const prevIsCreatingRef = useRef(isCreating);
  useEffect(() => {
    if (prevIsCreatingRef.current && !isCreating) {
      reattachListener();
      fetchPage(1);
    }
    prevIsCreatingRef.current = isCreating;
  }, [isCreating, fetchPage, reattachListener]);

  // Re-fetch from page 1 whenever sort or search changes
  useEffect(() => {
    if (isCreating) return;
    fetchPage(1);
  }, [fetchPage, isCreating]);

  // Poll for status updates when items are in processing state
  useEffect(() => {
    if (isCreating) return;
    const hasProcessingItems = items.some((item: TableListItem) => activeStatuses.has(item.status));
    if (!hasProcessingItems) return;

    const interval = setInterval(() => {
      fetchPage(1);
    }, 8000);

    return () => clearInterval(interval);
  }, [items, isCreating, fetchPage]);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'tables-list',
    steps: tablesListTourSteps,
    readyToShow: !isLoading && items.length > 0,
    autoStartDelay: 600,
  });

  const hasMore = currentPage < totalPages;

  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      fetchPage(currentPage + 1);
    }
  }, [fetchPage, currentPage, hasMore, isLoading]);

  // Debounced search handler
  const handleSearchChange = useCallback(
    debounce((query: string) => {
      setSearchTerm(query);
    }, 300),
    []
  );

  // ─── Table columns & data ──────────────────────────────────────────────

  const columns = ['Table Name', 'Status', 'Documents', 'Columns', 'Linked to', 'Access', 'Created', 'Actions'];

  const tableData = useMemo(
    () =>
      items.map((item: TableListItem) => {
        const liveState = processingStates[item.uuid];
        const isProcessing = item.status === 'processing';
        const isPending = item.status === 'pending';
        const isQueued = item.status === 'queued';
        const isFailed = item.status === 'failed';
        const isCompleted = item.status === 'completed';
        // Every table is enterable - the detail view renders live progress for
        // processing/queued/pending and the resumable state for stopped/failed,
        // so the user can always click in to watch or manage the run.
        const isClickable = true;

        return {
          id: item.uuid,
          uuid: item.uuid,
          processing_status: item.status,
          _isClickable: isClickable,
          'Table Name': (
            <div className="flex flex-col gap-0.5" style={{ opacity: isFailed ? 0.6 : 1 }}>
              {isProcessing ? (
                <div className="flex items-center gap-2">
                  <MiniLoadingToCheckmark isLoading={true} />
                  <div className="flex-1 min-w-0">
                    {liveState?.step ? (
                      <>
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm text-info-600 dark:text-info-400 font-medium truncate">
                            {(liveState.step || '').replace(/_/g, ' ')}
                          </span>
                        </div>
                        {liveState.message && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                            {liveState.message}
                          </div>
                        )}
                        {liveState.percent != null && liveState.percent > 0 && (
                          <div className="mt-1 h-1 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-info-500 to-info-700 transition-all duration-700 ease-in-out rounded-full"
                              style={{ width: `${liveState.percent}%` }}
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-gray-500 dark:text-gray-400 italic">{t('Processing table...')}</div>
                        <div className="h-3 w-2/3 bg-gray-200 dark:bg-gray-700 rounded mt-1 animate-fadePulse" />
                      </>
                    )}
                  </div>
                </div>
              ) : isFailed ? (
                <div className="flex items-center gap-2">
                  <div className="text-sm text-danger-500 dark:text-danger-400 italic">{t('Processing failed')}</div>
                </div>
              ) : (isPending || isQueued) ? (
                <div className="flex items-center gap-2">
                  <PiHourglassLight className="h-4 w-4 text-warning-400 animate-pulse flex-shrink-0" />
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                      {isPending ? t('Waiting to be queued...') : t('Queued for processing...')}
                    </div>
                  </div>
                </div>
              ) : (
                <span className="text-gray-900 dark:text-gray-100 truncate">{item.name}</span>
              )}
            </div>
          ),
          Status: (
            <div className="flex items-center gap-2">
              <Badge
                color={statusColorMap[item.status] || 'gray'}
                icon={
                  isProcessing ? <BrandLoader size="xs" /> :
                  (isPending || isQueued) ? <PiClockLight className="text-warning-400" /> :
                  undefined
                }
              >
                {statusLabelMap[item.status] || item.status}
              </Badge>
            </div>
          ),
        Documents: (
          <Badge color="blue">
            {item.document_count}
          </Badge>
        ),
        Columns: (
          <Badge color="gray">
            {item.column_count}
          </Badge>
        ),
        // The matter this table belongs to (feedback round 3).
        'Linked to': item.matter_uuid && item.matter_title ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); history.push(`/matters/${item.matter_uuid}`); }}
            className="inline-flex max-w-full items-center gap-1 truncate text-sm text-fg-body hover:text-champagne"
            title={item.matter_title}
          >
            <PiBriefcaseLight className="h-3.5 w-3.5 flex-shrink-0 text-fg-muted" />
            <span className="truncate">{item.matter_title}</span>
          </button>
        ) : <span className="text-xs text-fg-muted">—</span>,
        'Access': item.has_access && item.has_access.length > 0 ? (
          <CollaboratorStack collaborators={item.has_access} max={5} />
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
        Created: formatDate(item.created_at),
      };
    }),
    [items, processingStates]
  );

  const sortableColumns = ['Table Name', 'Status', 'Documents', 'Columns', 'Created'];

  // ─── Actions ───────────────────────────────────────────────────────────

  const handleRowClick = useCallback(
    (row: any) => {
      // Always enter the table - the detail view handles every state (live
      // processing/queued/pending, resumable stopped, and failed), so the user
      // can click in to watch the full process or manage/continue it.
      history.push(`/tables/${row.uuid}`);
    },
    [history]
  );

  const handleDelete = useCallback(
    (uuid: string) => {
      dispatch(
        showModal({
          type: 'custom',
          message: t('Are you sure you want to delete this table?'),
          subMsg: t('This action cannot be undone.'),
          icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
          width: '500px',
          content: (
            <DeleteTableConfirmation
              count={1}
              onConfirm={async () => {
                await dispatch(deleteBatchTable(uuid) as any);
                dispatch(hideModal());
              }}
              onCancel={() => dispatch(hideModal())}
            />
          ),
          onCancel: () => {},
          showModal: true,
        })
      );
    },
    [dispatch, t]
  );

  const handleReprocess = useCallback(
    async (uuid: string) => {
      await dispatch(startProcessing(uuid) as any);
    },
    [dispatch]
  );

  const handleReset = useCallback(
    (uuid: string) => {
      dispatch(
        showModal({
          type: 'confirmation',
          message: t('Reset table'),
          subMsg: t('All processed results will be cleared and the table will return to pending status.'),
          showModal: true,
          onConfirm: async () => {
            await dispatch(resetTable(uuid) as any);
            dispatch(hideModal());
          },
          onCancel: () => dispatch(hideModal()),
        })
      );
    },
    [dispatch]
  );

  const handleExport = useCallback(
    async (uuid: string) => {
      await dispatch(exportTable(uuid, 'csv') as any);
    },
    [dispatch]
  );

  const handleBulkDelete = useCallback(() => {
    if (selectedIds.length === 0) return;
    dispatch(
      showModal({
        type: 'custom',
        message: t(`Are you sure you want to delete ${selectedIds.length > 1 ? 'these tables' : 'this table'}?`),
        subMsg: t('This action cannot be undone.'),
        icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
        width: '500px',
        content: (
          <DeleteTableConfirmation
            count={selectedIds.length}
            onConfirm={async () => {
              if (selectedIds.length > 1) {
                await dispatch(deleteBatchTablesBulk(selectedIds) as any);
              } else {
                await dispatch(deleteBatchTable(selectedIds[0]) as any);
              }
              setSelectedIds([]);
              dispatch(hideModal());
            }}
            onCancel={() => dispatch(hideModal())}
          />
        ),
        onCancel: () => {},
        showModal: true,
      })
    );
  }, [dispatch, selectedIds, t]);

  const tableActions = useMemo(
    () => [
      {
        label: t('Activity'),
        onClick: (row: any) => {
          const item = items.find((i: TableListItem) => i.uuid === row.uuid);
          setAuditTarget({ type: 'table', id: row.uuid || row.id, name: item?.name || '' });
        },
        icon: <PiClockCounterClockwiseLight className="w-4 h-4" />,
      },
      // {
      //   label: t('Rerun'),
      //   onClick: (row: any) => handleReprocess(row.uuid),
      //   icon: <PiPlayLight className="w-4 h-4" />,
      // },
      {
        label: t('Export CSV'),
        onClick: (row: any) => handleExport(row.uuid),
        icon: <PiDownloadSimpleLight className="w-4 h-4" />,
      },
      // {
      //   label: t('Reset'),
      //   onClick: (row: any) => handleReset(row.uuid),
      //   icon: <PiArrowCounterClockwiseLight className="w-4 h-4" />,
      // },
      {
        label: t('Delete'),
        onClick: (row: any) => handleDelete(row.uuid),
        icon: <PiTrashLight className="w-4 h-4" />,
        isDestructive: true,
      },
    ],
    [handleDelete, handleReprocess, handleExport, handleReset, t, items]
  );

  const filterBarActions = useMemo(() => {
    const actions = [];
    if (selectedIds.length > 0) {
      actions.push({
        label: `Delete (${selectedIds.length})`,
        onClick: handleBulkDelete,
        icon: <PiTrashLight className="w-4 h-4" />,
      });
    }
    return actions;
  }, [selectedIds, handleBulkDelete]);

  const handleSortChange = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      // The Table component sends lowercase+underscore column names
      // Map them to the backend sort_by field names
      const columnToFieldMap: Record<string, string> = {
        'table_name': 'table_name',
        'status': 'status',
        'documents': 'documents',
        'columns': 'columns',
        'progress': 'progress',
        'created': 'created',
      };
      setSortBy(columnToFieldMap[column] || column);
      setSortOrder(order);
    },
    []
  );

  // ─── Create Modal ──────────────────────────────────────────────────────

  const handleCreateClose = useCallback(
    (newUuid?: string) => {
      if (newUuid) {
        history.push(`/tables/${newUuid}`);
      } else {
        history.replace('/tables');
      }
    },
    [history]
  );

  // ─── Render ────────────────────────────────────────────────────────────

  if (isCreating) {
    return (
      // `h-screen`, not `h-full`: PrivateLayout's <main> has no definite height
      // on non-scrollable routes (see BatchTablesPage), so a percentage height
      // here resolved to auto and the form grew to its content - which is what
      // pushed the action footer off the bottom of the viewport. Stating the
      // viewport height is what MessageCenterPage / ResearchPage already do for
      // their full-page routes. Everything below is a bounded flex column.
      <div className="flex flex-col overflow-hidden relative h-screen min-h-0">
        <div className="flex-1 min-h-0">
          <CreateTableForm onClose={handleCreateClose} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden relative">
      <div data-tour-id="tables-list-new-button">
        <Header
          actions={[
            {
              label: t('Add table'),
              icon: <PiPlusLight className="w-4 h-4" />,
              onClick: openCreate,
            },
          ]}
        />
      </div>

      <div data-tour-id="tables-list-filter-bar">
      <FilterBarWithTabs
        itemCount={showTrash ? trashCount.showing : items.length}
        totalItemCount={showTrash ? trashCount.total : totalItems}
        itemCountLabel={showTrash ? 'items' : 'tables'}
        listLoading={isLoading && items.length === 0}
        onSearchChange={handleSearchChange}
        // viewMode={viewMode}
        // setViewMode={setViewMode}
        actions={[
          ...filterBarActions,
          ...(showTrash ? trashBulkActions : []),
        ]}
        selectionCount={showTrash ? trashSelectedCount : (selectedIds.length > 0 ? selectedIds.length : undefined)}
        recycleBinEnabled
        recycleBinActive={showTrash}
        recycleBinLabel={t('Tables')}
        onRecycleBinToggle={(active) => { if (!active) fetchPage(1); setShowTrash(active); }}
        // onRefresh={() => fetchPage(1)}
        // itemCount={items.length}
        // totalItemCount={totalItems}
        // itemCountLabel="tables"
      />
      </div>

      {showTrash ? (
        <ResourceTrash
          onCountChange={(showing, total) => setTrashCount({ showing, total })}
          entityType="table"
          entityLabel="tables"
          emptyIcon={<PiTableLight />}
          onItemRestored={() => fetchPage(1)}
          onBulkActionsChange={setTrashBulkActions}
          onSelectedCountChange={setTrashSelectedCount}
        />
      ) : (
      /*
        ONE SCROLLER, and it is the table's.

        This box used to be `flex-grow overflow-y-auto` - a second scroll
        region wrapped around the one the table already has. The two disagreed
        about how tall the page was, and the arithmetic was visible: the outer
        box got 760px, the table rendered 818px inside it (bottom at 915, on an
        857px viewport), and its own region was capped at `calc(100vh-120px)`
        while actually starting 133px down. So the sticky header sat below the
        fold, the rows scrolled inside a box shorter than the page, "Load More"
        landed 50px past the bottom edge, and the leftover height showed as
        grey nothing underneath.

        It is a plain bounded flex column now: `flex-1 min-h-0` all the way
        down, so the table fills exactly the space that is left and does its
        own scrolling - with its header stuck to the top of the region a
        reader can actually see.
      */
      <div className="flex-1 min-h-0 relative flex flex-col">
        {/* Tour anchor: small overlay covering table header + first row area */}
        <div
          data-tour-id="tables-list-table"
          className="absolute inset-x-0 top-0 pointer-events-none"
          style={{ height: 120 }}
        />
        <div className="animate-fade-in flex-1 min-h-0 flex flex-col">
          <Table
            columns={columns}
            data={tableData}
            enableSelection
            selectedIds={selectedIds}
            onSelect={(id) =>
              setSelectedIds((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
              )
            }
            onSelectAll={() =>
              setSelectedIds(
                selectedIds.length === items.length
                  ? []
                  : items.map((item: TableListItem) => item.uuid)
              )
            }
            onRowClick={handleRowClick}
            actions={tableActions}
            hasMore={hasMore}
            isLoadingMore={isLoading}
            isLoading={isLoading}
            onLoadMore={loadMore}
            sortableColumns={sortableColumns}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSortChange}
            total_items={totalItems}
            total_showing_items={items.length}
            setIsShowing={true}
            showingLabel={t('tables')}
            selectedCount={selectedIds.length}
            resizableColumns
            storageKeyPrefix={t("tables")}
            /*
              NO floor and NO viewport-relative cap.

              `min-h-[calc(100vh-120px)]` was pinned to the same value as the
              cap, so the list was exactly one viewport tall whatever it held:
              two tables drew a full screen of empty striped body, and while
              the first page was still loading the skeleton filled the window
              under a "Showing 0 of 0" label.

              The cap had to go too. `100vh - 120px` is a GUESS at the chrome
              above this list, and the guess was wrong - the region starts
              133px down, so a 737px cap put its bottom 13px below the window.
              Now that the flex chain above is properly bounded, `flex-1
              min-h-0` sizes the region to exactly what is left, which is the
              measurement rather than an estimate of it.
            */
            max = 'max-h-none'
            defaultColumnWidths={{
              'Table Name': 250,
              Status: 120,
              Documents: 110,
              Columns: 100,
              'Linked to': 180,
              Progress: 140,
              'Has Access': 140,
              Created: 140,
            }}
            emptyState={{
              icon: <PiTableLight className="w-12 h-12" />,
              headline: t('No tables yet'),
              subtext: t(
                'Create a table to analyze batches of documents with AI-powered queries.'
              ),
              button: {
                label: t('Create first table'),
                onClick: openCreate,
              },
            }}
          />
        </div>
      </div>
      )}
      {/* Audit Log Side Panel */}
      {auditTarget && (
        <ActivityDrawer onClose={() => setAuditTarget(null)}>
          <AuditLogPanel
            entityType={auditTarget.type}
            entityId={auditTarget.id}
            entityName={auditTarget.name}
            onClose={() => setAuditTarget(null)}
          />
        </ActivityDrawer>
      )}

      <FeatureTour
        steps={tablesListTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
};

export default BatchTablesList;
